import { useQuery } from '@tanstack/react-query';
import { message, Select, Spin } from 'antd';
import _ from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';

import { fetchAPI } from '../../utils/api';

type SupplierLite = {
  id: number;
  name: string;
  country: string;
  marketplace?: {
    id: number;
    name: string;
  };
  is_gst_registered: boolean;
};

type SubmittedTicketProps = {
  region?: string;
  value?: number;
  defaultValue?: {
    id: number;
    name: string;
  };
  onChange: (supplierId?: number) => void;
};

const SubmitTicketSupplierDropdown = (props: SubmittedTicketProps) => {
  const [filters, setFilters] = useState({
    q: '',
    page: 1,
  });
  const [open, setOpen] = useState(false);

  const normalizedRegion =
    props.region && props.region !== 'AL' ? props.region : undefined;

  const suppliers = useQuery<{
    items: SupplierLite[];
    pagination: {
      page: number;
      per_page: number;
      total: number;
    };
  }>({
    queryKey: ['rfi-ticket-suppliers', normalizedRegion, filters],
    queryFn: async () => {
      try {
        const resp = await fetchAPI('suppliers/list-lite', {
          method: 'POST',
          payload: {
            ...filters,
            per_page: 50,
            region: normalizedRegion,
          },
        });

        return resp;
      } catch (error) {
        message.error('Failed get suppliers');
        return {
          items: [],
          pagination: { page: filters.page, per_page: 50, total: 0 },
        };
      }
    },
    placeholderData: {
      items: [],
      pagination: {
        page: filters.page,
        per_page: 50,
        total: 0,
      },
    },
    staleTime: 5 * 60 * 1000,
  });

  const supplierSearch = useMemo(
    () =>
      _.debounce((value: string) => {
        setFilters({ q: value, page: 1 });
      }, 500),
    []
  );

  useEffect(() => {
    setFilters({ q: '', page: 1 });
  }, [normalizedRegion]);

  useEffect(() => {
    return () => supplierSearch.cancel();
  }, [supplierSearch]);

  const hasNextPage =
    filters.page * (suppliers.data?.pagination.per_page || 0) <
    (suppliers.data?.pagination.total || 0);

  const handlePageChange = (direction: 'prev' | 'next') => {
    setFilters(current => ({
      ...current,
      page: direction === 'prev' ? current.page - 1 : current.page + 1,
    }));
    setOpen(true);
  };

  return (
    <Select
      placeholder="Select Supplier"
      showSearch
      filterOption={false}
      dropdownRender={menu => (
        <>
          {filters.page > 1 && !suppliers.isLoading && (
            <div
              onMouseDown={event => {
                event.preventDefault();
                event.stopPropagation();
              }}
              onClick={() => handlePageChange('prev')}
              style={{ cursor: 'pointer', padding: '5px 12px' }}
            >
              {'<< Prev'}
            </div>
          )}
          {menu}
          {hasNextPage && !suppliers.isLoading && (
            <div
              onMouseDown={event => {
                event.preventDefault();
                event.stopPropagation();
              }}
              onClick={() => handlePageChange('next')}
              style={{
                cursor: 'pointer',
                padding: '5px 12px',
                textAlign: 'right',
              }}
            >
              {'Next >>'}
            </div>
          )}
        </>
      )}
      onSearch={value => {
        supplierSearch.cancel();
        supplierSearch(value);
      }}
      loading={suppliers.isLoading}
      notFoundContent={suppliers.isFetching ? <Spin size="small" /> : null}
      onSelect={value => props.onChange(Number(value))}
      onClear={() => props.onChange(undefined)}
      value={props.value}
      allowClear
      open={open}
      onDropdownVisibleChange={visible => setOpen(visible)}
      style={{ width: '100%' }}
    >
      {(suppliers.data?.items || []).map(supplier => {
        const supplierText = `${supplier.name} (${
          supplier.marketplace?.name
        } - ${supplier.country}) ${
          supplier.is_gst_registered ? '[GST Registered]' : ''
        }`;
        return (
          <Select.Option value={supplier.id} key={supplier.id}>
            <span title={supplierText}>{supplierText}</span>
          </Select.Option>
        );
      })}
      {// If the current selected supplier is not in the list, add it to the top of the list
      props.value &&
        suppliers.data?.items.findIndex(item => item.id === props.value) ===
          -1 && (
          <Select.Option value={props.value} key={props.value}>
            <span title={props.defaultValue?.name}>
              {props.defaultValue?.name}
            </span>
          </Select.Option>
        )}
    </Select>
  );
};

export default SubmitTicketSupplierDropdown;
